import React, { useEffect, useId } from "react";

import {
  nothingScored,
  useDashboardResilience,
  Period,
  ResilienceReport,
} from "apps/company/api/rest/company-dashboard-resilience";
import { useTranslation } from "lib/i18n/use-translation";

import Dropdown from "../Dropdown";
import Info from "../Info";
import { useDashboardTracking } from "../tracking";
import Domains from "./Domains";
import Heatmap from "./Heatmap";
import Hero from "./Hero";
import Cohorts from "./Cohorts";
import {
  K,
  Section,
  useFreshness,
  useHalfLabel,
  VALIDATION_PAPER,
} from "./parts";
import SessionFeedback from "./SessionFeedback";
import StrengthsFeedback from "./StrengthsFeedback";
import Trend from "./Trend";
import styles from "./resilience.module.scss";

// In page order; each is filled by its own section of the page.
const SECTIONS = [
  "hero",
  "trendRepeat",
  "domains",
  "strengthsFeedback",
  "cohorts",
  "heatmap",
  "sessionFeedback",
] as const;

type SectionKey = typeof SECTIONS[number];

const BUILT: Partial<
  Record<
    SectionKey,
    (props: { report: ResilienceReport }) => JSX.Element | null
  >
> = {
  hero: Hero,
  domains: Domains,
  strengthsFeedback: StrengthsFeedback,
  sessionFeedback: SessionFeedback,
  trendRepeat: Trend,
  cohorts: Cohorts,
  heatmap: Heatmap,
};

const PeriodControl = ({
  periods,
  selected,
  onSelect,
}: {
  periods: Period[];
  selected: string | null;
  onSelect: (period: string) => void;
}) => {
  const { t } = useTranslation(["admin"]);
  const halfLabel = useHalfLabel();
  const id = useId();
  if (periods.length === 0) return null;
  if (periods.length === 1) {
    return (
      <p className={styles["period"]}>
        <span className={styles["period-label"]}>{t(`${K}.period`)}</span>
        <Info figure={t(`${K}.period`)} text={t(`${K}.periodInfo`)} />
        <strong className={styles["period-value"]}>
          {halfLabel(periods[0].value)}
        </strong>
      </p>
    );
  }
  return (
    <p className={styles["period"]}>
      <span id={id} className={styles["period-label"]}>
        {t(`${K}.period`)}
      </span>
      <Info figure={t(`${K}.period`)} text={t(`${K}.periodInfo`)} />
      <Dropdown
        labelId={id}
        value={selected ?? periods[0].value}
        onChange={onSelect}
        options={periods.map((period) => ({
          value: period.value,
          label: halfLabel(period.value),
        }))}
      />
    </p>
  );
};

const SectionPlaceholder = ({ section }: { section: SectionKey }) => {
  const { t } = useTranslation(["admin"]);
  return (
    <Section title={t(`${K}.sections.${section}`)}>
      <p className={styles["card-description"]}>{t(`${K}.comingSoon`)}</p>
    </Section>
  );
};

const ResilienceTab = () => {
  const { t } = useTranslation(["admin"]);
  const halfLabel = useHalfLabel();
  const freshness = useFreshness();
  const { status, periods, selected, open, select, retry } =
    useDashboardResilience();
  const { filterChanged } = useDashboardTracking();
  const selectHalf = (period: string) => {
    filterChanged("resilience", "half");
    select(period);
  };

  useEffect(() => {
    open();
  }, [open]);

  return (
    <div className={styles["resilience"]}>
      <header className={styles["header"]}>
        <div>
          <h2 className={styles["heading"]}>{t(`${K}.title`)}</h2>
          <p className={styles["subheading"]}>{t(`${K}.subtitle`)}</p>
        </div>
        <div className={styles["header-side"]}>
          <PeriodControl
            periods={periods}
            selected={selected}
            onSelect={selectHalf}
          />
          {periods.length > 0 && (
            <p className={styles["freshness"]}>
              {freshness(selected ?? periods[0].value, periods[0].value)}
            </p>
          )}
        </div>
      </header>

      {(status.state === "idle" || status.state === "loading") && (
        <div className={styles["status"]} role="status">
          <h3 className={styles["card-title"]}>{t(`${K}.loading.title`)}</h3>
          <p className={styles["card-description"]}>{t(`${K}.loading.body`)}</p>
        </div>
      )}

      {status.state === "error" && (
        <div className={styles["status"]} role="alert">
          <p className={styles["card-description"]}>{t(`${K}.error`)}</p>
          <button
            type="button"
            className={styles["retry"]}
            onClick={() => retry()}
          >
            {t(`${K}.retry`)}
          </button>
        </div>
      )}

      {status.state === "ready" &&
        (nothingScored(status.report.scores.selected) ? (
          <div className={styles["status"]} role="status">
            <h3 className={styles["card-title"]}>
              {t(`${K}.nothingScored.title`)}
            </h3>
            <p className={styles["card-description"]}>
              {t(`${K}.nothingScored.body`, {
                period: halfLabel(selected as string),
              })}
            </p>
          </div>
        ) : (
          <div className={styles["sections"]}>
            {SECTIONS.map((section) => {
              const Built = BUILT[section];
              return Built ? (
                <Built
                  key={
                    section === "cohorts"
                      ? `${section}:${status.report.period_value}`
                      : section
                  }
                  report={status.report}
                />
              ) : (
                <SectionPlaceholder key={section} section={section} />
              );
            })}
          </div>
        ))}

      <p className={styles["page-footnote"]}>
        <span aria-hidden="true">{t(`${K}.validation.marker`)} </span>
        {t(`${K}.validation.citation`)}{" "}
        <a href={VALIDATION_PAPER} target="_blank" rel="noopener noreferrer">
          {t(`${K}.validation.readPaper`)}
          <span className={styles["visually-hidden"]}>
            {" "}
            {t(`${K}.validation.newTab`)}
          </span>
        </a>
      </p>
    </div>
  );
};

export default ResilienceTab;
